import { useAtomValue } from "jotai";
import { useEffect } from "react";
import {
  setUserId as setDatadogUserId,
  setUserVars as setDatadogUserVars,
} from "../datadog/init";
import { logger } from "../datadog/logger";
import {
  setUserId as setFullstoryUserId,
  setUserVars as setFullstoryUserVars,
} from "../fullstory/init";
import { ExperimentLayers } from "../statsig/experiments";
import { Statsig } from "../statsig/StatsigProvider";
import {
  participantSidsAtom,
  roomAtom,
  roomToDiagnosticInfo,
  useStoredAudioVideoSettings,
} from "../video-sdk";
import { useVideoPlatform } from "./useVideoPlatform";
import { isVirtualBackgroundSupportedAtom } from "../video-sdk/core/atoms";

function getExperimentParameters() {
  const parameters: Record<string, unknown> = {};

  Object.values(ExperimentLayers).forEach(([name, parameterMap]) => {
    Object.values(parameterMap).forEach((p) => {
      parameters[p] = Statsig?.getLayer(name, {
        disableExposureLog: true,
      })?.__value[p];
    });
  });

  return parameters;
}

/**
 * Sets application-specific FullStory / Datadog vars i.e. all sessions will
 * have this information. This should help debug with application/user-specific
 * context.
 *
 * You can add info to the global context as desired, but take care to not add
 * PHI/PII.
 */
export function useInitUserTracking(
  userId?: string,
  additionalVars: Record<string, unknown> = {},
) {
  const room = useAtomValue(roomAtom);
  const remoteParticipantSids = useAtomValue(participantSidsAtom);
  const storedAudioVideoSettings =
    useStoredAudioVideoSettings()?.storedAudioVideoSettings;
  const videoPlatform = useVideoPlatform();
  const isVirtualBackgroundSupported = useAtomValue(
    isVirtualBackgroundSupportedAtom,
  );

  useEffect(
    function setIdentity() {
      if (!userId) return;
      try {
        setFullstoryUserId(userId);
        setDatadogUserId(userId);
      } catch (error) {
        logger.error("Unable to set identity", {}, error as Error);
      }
    },
    [userId],
  );

  useEffect(
    function setCustomVars() {
      try {
        const opts = {
          storedAudioVideoSettings: storedAudioVideoSettings,
          ...(room
            ? {
                twilio: roomToDiagnosticInfo(room),
              }
            : {}),
          remoteParticipantSids,
          videoPlatform,
          isVirtualBackgroundSupported,
          ...additionalVars,
          experimentParameters: getExperimentParameters(),
        };

        setFullstoryUserVars(opts);
        setDatadogUserVars(opts);
      } catch (error) {
        logger.error("Unable to set user vars", {}, error as Error);
      }
    },
    [
      additionalVars,
      remoteParticipantSids,
      storedAudioVideoSettings,
      room,
      videoPlatform,
      isVirtualBackgroundSupported,
    ],
  );
}
